import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { CalendarEvent } from '../api/types';
import { useUser } from '../state/UserContext';
import { TRADITION_LABELS } from '../theme/tokens';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDay(dateStr: string): { month: string; day: number; weekday: string } {
  // Parse as a plain calendar date (no timezone shift) rather than via `new Date(dateStr)`,
  // which would interpret an ISO date-only string as UTC midnight and can roll to the wrong
  // local day near midnight.
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    month: MONTH_NAMES[dt.getMonth()],
    day: dt.getDate(),
    weekday: dt.toLocaleDateString(undefined, { weekday: 'long' }),
  };
}

function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** One row of event detail, shared between the list view's inline card and the grid view's
 * selected-day panel, so the two views never drift into showing different information. */
function EventDetail({ e, isNext }: { e: CalendarEvent; isNext: boolean }) {
  return (
    <div className="calendar-row-info">
      <div className="calendar-row-title">
        {e.title}
        {isNext && <span className="calendar-next-pill">Next up</span>}
      </div>
      <div className="calendar-row-meta">
        <span className="calendar-tradition-pill">{TRADITION_LABELS[e.tradition] ?? e.tradition}</span>
        {e.movable && <span title="Date depends on lunar/solar sighting and shifts each year"> &middot; date varies by year</span>}
      </div>
      <p className="calendar-row-description">{e.description}</p>
      {(e.region || e.community || e.startTime) && (
        <div className="calendar-row-richness">
          {e.region && (
            <p className="calendar-row-richness-item">
              <span className="calendar-row-richness-label">Region</span> {e.region}
            </p>
          )}
          {e.community && (
            <p className="calendar-row-richness-item">
              <span className="calendar-row-richness-label">Community</span> {e.community}
            </p>
          )}
          {e.startTime && (
            <p className="calendar-row-richness-item">
              <span className="calendar-row-richness-label">Start</span> {e.startTime}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function Calendar() {
  const { me } = useUser();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'grid'>('list');
  // {year, month} with month 0-indexed (JS Date convention) -- initialized once events load, to
  // today's month if it has any seeded data, else the first month that actually has an event.
  const [gridMonth, setGridMonth] = useState<{ year: number; month: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getCalendar()
      .then((res) => {
        if (!cancelled) setEvents(res.events);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the calendar.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const today = todayIsoDate();
  const sorted = useMemo(() => [...events].sort((a, b) => a.date.localeCompare(b.date)), [events]);
  const nextUpcomingId = useMemo(() => sorted.find((e) => e.date >= today)?.eventId, [sorted, today]);

  // Group into month buckets ("2026-09" -> events), preserving chronological order.
  const monthGroups = useMemo(() => {
    const groups = new Map<string, CalendarEvent[]>();
    for (const e of sorted) {
      const key = e.date.slice(0, 7);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    return Array.from(groups.entries());
  }, [sorted]);

  // Every event keyed by its exact date, for O(1) grid-cell lookups.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of sorted) {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date)!.push(e);
    }
    return map;
  }, [sorted]);

  // Default the grid to today's month if it has any seeded data, else the first month that
  // actually has an event -- never an arbitrary empty month on first load.
  useEffect(() => {
    if (gridMonth || sorted.length === 0) return;
    const todayMonth = today.slice(0, 7);
    const hasToday = sorted.some((e) => e.date.slice(0, 7) === todayMonth);
    const [y, m] = (hasToday ? todayMonth : sorted[0].date.slice(0, 7)).split('-').map(Number);
    setGridMonth({ year: y, month: m - 1 });
  }, [sorted, gridMonth, today]);

  function shiftGridMonth(delta: number) {
    setGridMonth((prev) => {
      if (!prev) return prev;
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
    setSelectedDate(null);
  }

  // Standard 7-column month grid: leading/trailing blanks so the 1st lands under its real weekday.
  const gridCells = useMemo(() => {
    if (!gridMonth) return [];
    const { year, month } = gridMonth;
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ date: string; day: number } | null> = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`, day });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [gridMonth]);

  const selectedEvents = selectedDate ? eventsByDate.get(selectedDate) ?? [] : [];

  return (
    <div>
      <h1 className="page-title">Calendar</h1>
      <p className="page-subtitle">
        Sacred dates and festivals across your traditions, through the year.
      </p>

      {!loading && sorted.length > 0 && (
        <div className="calendar-view-toggle" role="tablist" aria-label="Calendar view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            className={`calendar-view-toggle-btn${view === 'list' ? ' active' : ''}`}
            onClick={() => setView('list')}
          >
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'grid'}
            className={`calendar-view-toggle-btn${view === 'grid' ? ' active' : ''}`}
            onClick={() => setView('grid')}
          >
            Grid
          </button>
        </div>
      )}

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="empty-state">
          No calendar events match your traditions yet. Try adding more in Settings.
        </div>
      ) : view === 'list' ? (
        <div className="calendar-list">
          {monthGroups.map(([monthKey, monthEvents]) => {
            const [, m] = monthKey.split('-').map(Number);
            return (
              <section key={monthKey} className="calendar-month">
                <h2 className="calendar-month-title">{MONTH_NAMES[m - 1]}</h2>
                {monthEvents.map((e) => {
                  const { day, weekday } = formatDay(e.date);
                  const isPast = e.date < today;
                  const isNext = e.eventId === nextUpcomingId;
                  return (
                    <div
                      key={e.eventId}
                      className={`calendar-row${isPast ? ' past' : ''}${isNext ? ' next' : ''}`}
                    >
                      <div className="calendar-date-badge">
                        <span className="calendar-date-day">{day}</span>
                        <span className="calendar-date-weekday">{weekday.slice(0, 3)}</span>
                      </div>
                      <EventDetail e={e} isNext={isNext} />
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      ) : (
        gridMonth && (
          <div className="calendar-grid-wrap">
            <div className="calendar-grid-nav">
              <button type="button" className="calendar-grid-nav-btn" onClick={() => shiftGridMonth(-1)} aria-label="Previous month">
                &larr;
              </button>
              <h2 className="calendar-month-title">
                {MONTH_NAMES[gridMonth.month]} {gridMonth.year}
              </h2>
              <button type="button" className="calendar-grid-nav-btn" onClick={() => shiftGridMonth(1)} aria-label="Next month">
                &rarr;
              </button>
            </div>

            <div className="calendar-grid-weekdays">
              {WEEKDAY_SHORT.map((w) => (
                <div key={w} className="calendar-grid-weekday">{w}</div>
              ))}
            </div>

            <div className="calendar-grid">
              {gridCells.map((cell, i) => {
                if (!cell) return <div key={`blank-${i}`} className="calendar-grid-cell empty" />;
                const dayEvents = eventsByDate.get(cell.date) ?? [];
                const isToday = cell.date === today;
                const isSelected = cell.date === selectedDate;
                return (
                  <button
                    type="button"
                    key={cell.date}
                    className={`calendar-grid-cell${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}${dayEvents.length ? ' has-events' : ''}`}
                    onClick={() => dayEvents.length > 0 && setSelectedDate(cell.date === selectedDate ? null : cell.date)}
                    disabled={dayEvents.length === 0}
                  >
                    <span className="calendar-grid-cell-day">{cell.day}</span>
                    {dayEvents.length > 0 && (
                      <span className="calendar-grid-cell-dots">
                        {dayEvents.slice(0, 3).map((e) => (
                          <span key={e.eventId} className="calendar-grid-cell-dot" title={e.title} />
                        ))}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {selectedEvents.length > 0 && (
              <div className="calendar-grid-detail">
                {selectedEvents.map((e) => (
                  <div key={e.eventId} className="calendar-row calendar-grid-detail-row">
                    <EventDetail e={e} isNext={e.eventId === nextUpcomingId} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      )}

      {!loading && sorted.length > 0 && (
        <p className="calendar-footnote">
          {me?.traditions?.length
            ? "Showing observances for your selected traditions."
            : 'Showing observances across all traditions — select yours in Settings to narrow this down.'}
        </p>
      )}
    </div>
  );
}
